import { useEffect, useState } from "react";
import { FaLink, FaPlus, FaArchive, FaStar, FaRegStar, FaExternalLinkAlt, FaPen, FaChartBar } from "react-icons/fa";
import { LINKS_CONSTANTS } from "../_MODULES/_API";
import { OPTIONS_LINK_CATEGORY, onFindOptionLabel } from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FormGrid, FieldInput, FieldSelect, FieldTextarea, FieldSwitch, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { useDateRange, onMatchesRange } from "../../../../_UI/useDateRange";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";

const EMPTY_VALUES = { title: "", url: "", description: "", category: "OTHER", isFavorite: false };

export const PanelLinks = ({ projectId }) => {

    const [useSearch, setSearch] = useState("");
    const [statisticsOpen, setStatisticsOpen] = useState(false);

    const DATE_RANGE = useDateRange();
    const [useIsModalOpen, setIsModalOpen] = useState(false);
    const [useLinkEdit, setLinkEdit] = useState(null);

    const { apiGet: onGetLinks, useApiData: LINKS, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 300,
    });

    const onLoadLinks = () => onGetLinks({
        endpoint: LINKS_CONSTANTS.BY_PROJECT, isDetail: true, config: { pathParams: { projectId } },
    });

    useEffect(() => { onLoadLinks(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const FORM = useForm({
        initialValues: EMPTY_VALUES,

        schema: { title: { required: true }, url: { required: true } },

        onSubmit: async (values) => {

            const IS_EDIT = !!useLinkEdit?.id;

            const PAYLOAD = { ...values, description: values.description || "" };

            if (!IS_EDIT) PAYLOAD.projectId = projectId;

            const RESPONSE = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: LINKS_CONSTANTS.UPDATE, successMessage: "Enlace actualizado", id: useLinkEdit.id, data: PAYLOAD })
                : await actionMutate({ method: "POST", endpoint: LINKS_CONSTANTS.CREATE, successMessage: "Enlace agregado", data: PAYLOAD });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            /*
                El servidor ya devolvio la fila guardada: la insertamos o la
                parcheamos en la lista que ya tenemos. Sin recargar y sin
                una segunda peticion.
            */
            mutateLocal(IS_EDIT
                ? { id: useLinkEdit.id, patch: RESPONSE.data }
                : { insert: RESPONSE.data });

            FORM.reset();
            setLinkEdit(null);
            setIsModalOpen(false);
        },
    });

    const onOpenCreate = () => {
        setLinkEdit(null);
        FORM.setValues(EMPTY_VALUES);
        setIsModalOpen(true);
    };

    const onOpenEdit = (link) => {
        setLinkEdit(link);
        FORM.setValues({
            title: link.title ?? "",
            url: link.url ?? "",
            description: link.description ?? "",
            category: link.category ?? "OTHER",
            isFavorite: link.isFavorite ?? false,
        });
        setIsModalOpen(true);
    };

    const onCloseModal = () => {
        setIsModalOpen(false);
        setLinkEdit(null);
    };

    const onDelete = async (link) => {

        const IS_CONFIRMED = await confirm.danger("Archivar enlace", `Se archiva "${link.title}".`);

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: LINKS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: link.id }, successMessage: "Enlace archivado",
        });

        if (RESPONSE.success) mutateLocal({ id: link.id, remove: true });
    };

    const onToggleFavorite = async (link) => {
        const previous = !!link.isFavorite;
        const next = !previous;
        mutateLocal({ id: link.id, patch: { isFavorite: next } });

        const RESPONSE = await onSilentPatch({
            endpoint: LINKS_CONSTANTS.UPDATE,
            id: link.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del enlace.",
        });

        if (!RESPONSE.success) mutateLocal({ id: link.id, patch: { isFavorite: previous } });
    };

    const ITEMS = [...(LINKS ?? [])]
        .sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || String(a.title ?? "").localeCompare(String(b.title ?? "")));

    const VISIBLE = ITEMS.filter((link) => onMatchesRange(link, DATE_RANGE.useRange, "createdAt"))
        .filter((link) => onMatchesSearch(useSearch, [link.title, link.url, link.description, link.category]));

    const PAGER = usePagedList(VISIBLE, 6, "linksPage");

    return (
        <>
            <div className="LStudio__app_panel_bar">


                <ViewToolbar
                    filters={
                        <SearchFilter value={useSearch} onChange={setSearch} total={ITEMS.length} matches={VISIBLE.length}
                            placeholder="Ej.: GitLab, producción o documentación..." />
                    }
                    dateRange={DATE_RANGE}
                    total={PAGER.total} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={[
                        { key: "title", label: "Título" },
                        { key: "url", label: "Dirección" },
                        { key: "category", label: "Categoría" },
                        { key: "description", label: "Descripción" },
                    ]}
                    exportRows={VISIBLE.map((link) => ({
                        title: link.title, url: link.url,
                        category: onFindOptionLabel(OPTIONS_LINK_CATEGORY, link.category),
                        description: link.description ?? "",
                    }))}
                    exportName="enlaces"
                    actions={<>
                        <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly
                            title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                        <AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo enlace</AppButton>
                    </>} />

            </div>

            {IS_LOADING && <Skeleton variant="box" height={140} />}

            {
                !IS_LOADING && VISIBLE.length === 0 && (
                    <EmptyState icon={<FaLink />} title="Sin enlaces guardados"
                        description="Repositorio, panel de deploy, documentación, diseño." />
                )
            }

            {
                !IS_LOADING && VISIBLE.length > 0 && (
                    <ul className="LStudio__app_links_list">
                        {
                            PAGER.paged.map((link) => (
                                <li key={link.id} className="LStudio__app_links_item">

                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                        icon={link.isFavorite ? <FaStar /> : <FaRegStar />}
                                        aria-label={link.isFavorite ? "Quitar de destacados" : "Destacar enlace"}
                                        title={link.isFavorite ? "Quitar de destacados" : "Destacar enlace"}
                                        onClick={() => onToggleFavorite(link)} />

                                    <a href={link.url} target="_blank" rel="noreferrer" className="LStudio__app_links_item_main">

                                        <span className="LStudio__app_links_item_title">{link.title} <FaExternalLinkAlt /></span>

                                        <span className="LStudio__app_links_item_url">{link.url}</span>

                                        {link.description && <span className="LStudio__app_links_item_desc">{link.description}</span>}

                                    </a>

                                    <Badge tone="neutral" size="sm">{onFindOptionLabel(OPTIONS_LINK_CATEGORY, link.category)}</Badge>

                                    <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaPen />}
                                        isIconOnly aria-label="Editar enlace" onClick={() => onOpenEdit(link)} />

                                    <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />}
                                        isIconOnly aria-label="Archivar enlace" onClick={() => onDelete(link)} />

                                </li>
                            ))
                        }
                    </ul>
                )
            }

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Enlaces" description="Resumen de enlaces guardados en el proyecto."
                metrics={[
                    { key: "links", label: "Enlaces", value: ITEMS.length, icon: <FaLink />, tone: "neutral", isLoading: IS_LOADING },
                    { key: "favorites", label: "Destacados", value: ITEMS.filter((link) => link.isFavorite).length, icon: <FaStar />, tone: "accent", isLoading: IS_LOADING },
                    { key: "categories", label: "Categorías", value: new Set(ITEMS.map((link) => link.category).filter(Boolean)).size, icon: <FaLink />, tone: "neutral", isLoading: IS_LOADING },
                ]} />

            <AppModal isOpen={useIsModalOpen} onClose={onCloseModal}
                isDirty={FORM.isDirty} size="md"
                title={useLinkEdit ? "Editar enlace" : "Nuevo enlace"}
                footer={
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={onCloseModal}>Cancelar</AppButton>
                        <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>
                            {useLinkEdit ? "Guardar cambios" : "Guardar"}
                        </AppButton>
                    </>
                } >

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="isFavorite" label="Destacado" text="Mostrar primero"
                    isChecked={FORM.values.isFavorite}
                    onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

                <FormGrid columns={2}>

                    <FieldInput {...FORM.field("title")} label="Título" placeholder="Ej: Base de datos de produccion" />

                    <FieldInput {...FORM.field("url")} label="Dirección" placeholder="Ej: https://app.example.com" />

                    <FieldTextarea {...FORM.field("description")} label="Descripción" rows={2} span={2} placeholder="Ej: Portal interno de operaciones" />

                    <FieldSelect {...FORM.field("category")} label="Categoría" options={OPTIONS_LINK_CATEGORY} isSearchable />


                </FormGrid>

            </AppModal>
        </>
    );
};
